import { compact, isEmpty, unionBy } from 'lodash';
import { useMemo } from 'react';

import * as Models from 'contracts/models';
import useGetRobotName from 'hooks/use-get-robot-name';
import { mdToPlain } from 'utils/md-to-plain';
import parseDate from 'utils/parse-date';
import toMeaningfulFixed from 'utils/to-meaningful-fixed';
import Utils from 'utils/Utils';

import { FormattedDataItem } from '../types';

interface Input {
    items: Models.Search.Item[];
    exact?: Models.Search.Item | null;
}

export default function useFormattedData({ items, exact }: Input): FormattedDataItem[] {
    const getRobotName = useGetRobotName();

    return useMemo(
        () =>
            unionBy(compact([exact]), compact(items), ({ _id }) => _id).map((item) => {
                return {
                    image: Utils.getImage(item._source.type),
                    type: getRobotName(item._source.type),
                    title: item._source.title,
                    description: Utils.trunc(
                        300,
                        mdToPlain(item.flatDescription || '') || mdToPlain(item._source.description || '') || ''
                    ),
                    published: parseDate(item._source.published),
                    views: item._source.viewCount,
                    exact: exact?._id === item._id,
                    exploits: item._source.exploits_count ?? null,
                    references: item._source.references_count ?? null,
                    affectedSoftware: item._source.affectedSoftware_count ?? null,
                    affectedPackages: item._source.affectedPackages?.length ?? null,
                    affectedConfiguration: item._source.affectedConfiguration?.length ?? null,
                    cvssScore: item._source.cvss?.score ?? null,
                    aiScore: item._source.enchantments?.vulnersScore ?? item._source.enchantments?.score?.value ?? null,
                    epssScore: isEmpty(item._source.epss)
                        ? null
                        : Number(toMeaningfulFixed(Math.max(...item._source.epss.map(({ epss }) => epss)), 5)),
                    isWild:
                        item._source.enchantments?.exploitation?.wildExploited ?? item._source.wildExploited ?? false,
                    tags: item._source.enchantments?.tags ?? [],
                    href: `/${item._source.type}/${item._source.id}`,
                    webApplicable: item._source.webApplicability?.applicable
                };
            }),
        [exact, getRobotName, items]
    );
}
